import React from 'react';
import { array, func, string, number, object } from 'prop-types';
import StarSVG from 'library/icons/star.svg';
import EmptyStarSVG from 'library/icons/star-empty.svg';
import useWithA11y from 'library/hooks/use-with-a11y';
import KeyCodes from 'library/helpers/key-codes';
import CheckboxTitle from 'apps/journey/components/checkbox/checkbox-title';
import IndustryTag from 'apps/journey/pages/career-exploration/components/industry-tag/industry-tag';
import {
  StyledFilterDropdownOption,
  StyledFilterDropdownInlineOption,
  StyledLabel,
  StyledStar,
  StyledSalarySign,
  SignContainer,
} from 'apps/journey/pages/career-exploration/components/filters/styled-filter-types';

export const FilterTypes = {
  EDUCATION_FILTER: 'EDUCATION_FILTER',
  SAVED_FILTER: 'SAVED_FILTER',
  SALARY_FILTER: 'SALARY_FILTER',
  CAREER_CLUSTER_FILTER: 'CAREER_CLUSTER_FILTER',
};

const FilterType = ({
  filterOption,
  filterIndex,
  filterType = FilterTypes.EDUCATION_FILTER,
  filters = [],
  handleFilterUpdate = () => null,
}) => {
  const filterUpdate = (e) => {
    const newFilters = e.value
      ? [...filters, e.filterOption]
      : filters.filter((x) => x !== e.filterOption);
    handleFilterUpdate(newFilters);
  };

  const updateSavedFilter = (saved, name) => {
    return () => filterUpdate({ value: !saved, filterOption: name });
  };

  const handleKeyPress = (e, actionCallback) => {
    e.preventDefault();
    if (KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
      actionCallback();
    }
  };

  const { isTabbing } = useWithA11y();

  if (filterType === FilterTypes.EDUCATION_FILTER) {
    const isChecked = !!filters.find((filter) => filter === filterOption);
    const updateCheckboxFilter = () => {
      filterUpdate({ value: !isChecked, filterOption: filterOption });
    };
    return (
      <StyledFilterDropdownOption
        onKeyPress={(e) => handleKeyPress(e, updateCheckboxFilter)}
      >
        <CheckboxTitle
          tabIndex={0}
          isTabbing={isTabbing}
          name={filterOption}
          isChecked={isChecked}
          handleChange={updateCheckboxFilter}
        >
          {filterOption}
        </CheckboxTitle>
      </StyledFilterDropdownOption>
    );
  }
  if (filterType === FilterTypes.CAREER_CLUSTER_FILTER) {
    const isChecked = !!filters.find((filter) => filter === filterOption.id);
    const updateCheckboxFilter = () => {
      filterUpdate({ value: !isChecked, filterOption: filterOption.id });
    };
    return (
      <StyledFilterDropdownOption
        onKeyPress={(e) => handleKeyPress(e, updateCheckboxFilter)}
      >
        <CheckboxTitle
          tabIndex={0}
          isTabbing={isTabbing}
          name={filterOption.id}
          isChecked={isChecked}
          handleChange={updateCheckboxFilter}
        >
          <IndustryTag noMargin name={filterOption.title} />
        </CheckboxTitle>
      </StyledFilterDropdownOption>
    );
  } else if (filterType === FilterTypes.SAVED_FILTER) {
    const name = filterOption.name;
    const saved = !!filters.find((filter) => filter === name);
    return (
      <StyledFilterDropdownOption>
        <StyledStar
          onClick={updateSavedFilter(saved, name)}
          aria-label={`${saved ? 'Saved' : 'All'} occupations`}
          aria-pressed={saved}
          tabIndex={0}
          isTabbing={isTabbing}
          onKeyPress={(e) => handleKeyPress(e, updateSavedFilter(saved, name))}
        >
          {saved ? (
            <StarSVG width="21px" height="21px" />
          ) : (
            <EmptyStarSVG width="21px" height="21px" />
          )}
        </StyledStar>
        <StyledLabel activeFilter={saved}>{name}</StyledLabel>
      </StyledFilterDropdownOption>
    );
  } else if (filterType === FilterTypes.SALARY_FILTER) {
    const currentFilter = filters.some((filter) => filter === filterIndex);
    const min = Math.min(...filters);
    const max = Math.max(...filters);
    if (!currentFilter && filters.length > 1) {
      if (filterIndex > min && filterIndex < max) {
        filterUpdate({ value: true, filterOption: filterIndex });
      }
    }
    const handleRangeUpdate = () => {
      filterUpdate({ value: !currentFilter, filterOption: filterIndex });
    };

    return (
      <StyledFilterDropdownInlineOption
        tabIndex={0}
        isTabbing={isTabbing}
        onClick={handleRangeUpdate}
        onKeyPress={(e) => handleKeyPress(e, handleRangeUpdate)}
        activeFilter={currentFilter}
        first={filterIndex === 0}
        last={filterIndex === 3}
      >
        <SignContainer>
          <StyledSalarySign salaryFontSize={[10, 12, 14, 16][filterIndex]} />
        </SignContainer>
      </StyledFilterDropdownInlineOption>
    );
  }
  return null;
};

FilterType.propTypes = {
  filterOption: object,
  filterIndex: number,
  filterType: string,
  filters: array,
  handleFilterUpdate: func,
};

export default React.memo(FilterType);
